// Requêtes des écrans des interventions (TanStack Query). Clés sous « interventions » ; le
// changement de contexte vide le cache (FournisseurSession).
import type { DecisionConflitTerrain } from '@cc/contrats';
import { useQuery } from '@tanstack/react-query';
import { requete } from '../../api/client';
import type {
  Conflit,
  CreationEquipe,
  CreationIntervention,
  CreationMission,
  CreationSignalementAgent,
  Equipe,
  Intervention,
  InterventionDetail,
  Liste,
  MissionDetail,
  ModificationMission,
  Prestataire,
  Qualification,
  Rapport,
  Signalement,
  SignalementDetail,
} from './types';

export const CLES = {
  signalements: (f: Record<string, string>) => ['interventions', 'signalements', f] as const,
  signalement: (id: string) => ['interventions', 'signalement', id] as const,
  interventions: (f: Record<string, string>) => ['interventions', 'liste', f] as const,
  intervention: (id: string) => ['interventions', 'fiche', id] as const,
  mission: (id: string) => ['interventions', 'mission', id] as const,
  equipes: ['interventions', 'equipes'] as const,
  prestataires: ['interventions', 'prestataires'] as const,
  conflits: (etat: string) => ['interventions', 'conflits', etat] as const,
};

export const INVALIDER_INTERVENTIONS: readonly (readonly unknown[])[] = [['interventions'], ['indicateurs']];

export function useSignalements(f: Record<string, string>) {
  return useQuery({
    queryKey: CLES.signalements(f),
    queryFn: ({ signal }) => requete<Liste<Signalement>>('/signalements', { signal, parametres: f }).then((r) => r.elements),
    retry: false,
  });
}

export function useSignalement(id: string | null) {
  return useQuery({
    queryKey: CLES.signalement(id ?? ''),
    queryFn: ({ signal }) => requete<SignalementDetail>(`/signalements/${id}`, { signal }),
    enabled: Boolean(id),
    retry: false,
  });
}

export function useInterventions(f: Record<string, string>) {
  return useQuery({
    queryKey: CLES.interventions(f),
    queryFn: ({ signal }) => requete<Liste<Intervention>>('/interventions', { signal, parametres: f }).then((r) => r.elements),
    retry: false,
  });
}

export function useIntervention(id: string | null) {
  return useQuery({
    queryKey: CLES.intervention(id ?? ''),
    queryFn: ({ signal }) => requete<InterventionDetail>(`/interventions/${id}`, { signal }),
    enabled: Boolean(id),
    retry: false,
  });
}

export function useMission(id: string | null) {
  return useQuery({
    queryKey: CLES.mission(id ?? ''),
    queryFn: ({ signal }) => requete<MissionDetail>(`/interventions/missions/${id}`, { signal }),
    enabled: Boolean(id),
    retry: false,
  });
}

export function useEquipes(actif = true) {
  return useQuery({
    queryKey: CLES.equipes,
    queryFn: ({ signal }) => requete<Liste<Equipe>>('/interventions/equipes', { signal }).then((r) => r.elements),
    enabled: actif,
    retry: false,
  });
}

export function usePrestataires(actif = true) {
  return useQuery({
    queryKey: CLES.prestataires,
    queryFn: ({ signal }) => requete<Liste<Prestataire>>('/interventions/prestataires', { signal }).then((r) => r.elements),
    enabled: actif,
    retry: false,
  });
}

export function useConflits(etat: 'en_attente' | 'tous') {
  return useQuery({
    queryKey: CLES.conflits(etat),
    queryFn: ({ signal }) => requete<Liste<Conflit>>('/terrain/conflits', { signal, parametres: { etat } }).then((r) => r.elements),
    retry: false,
  });
}

// --- écritures -----------------------------------------------------------------------------

export const qualifierSignalement = (id: string, q: Qualification) => requete<SignalementDetail>(`/signalements/${id}/qualifier`, { methode: 'POST', corps: q });
export const creerSignalement = (corps: CreationSignalementAgent) => requete<SignalementDetail>('/signalements', { methode: 'POST', corps });
export const creerIntervention = (corps: CreationIntervention) => requete<InterventionDetail>('/interventions', { methode: 'POST', corps });
export const transiterIntervention = (id: string, code: string, corps: { motif?: string; planifiee_le?: string; cout_reel?: number }) =>
  requete<InterventionDetail>(`/interventions/${id}/transitions/${code}`, { methode: 'POST', corps });
export const creerMission = (interventionId: string, corps: CreationMission) => requete<MissionDetail>(`/interventions/${interventionId}/missions`, { methode: 'POST', corps });
export const modifierMission = (id: string, corps: ModificationMission) => requete<MissionDetail>(`/interventions/missions/${id}`, { methode: 'PATCH', corps });
export const annulerMission = (id: string, motif: string) => requete<MissionDetail>(`/interventions/missions/${id}/annuler`, { methode: 'POST', corps: { motif } });
export const rapportEnLigne = (missionId: string, corps: { type: string; contenu: Rapport['contenu'] }) => requete<Rapport>(`/interventions/missions/${missionId}/rapports`, { methode: 'POST', corps });
export const creerEquipe = (corps: CreationEquipe) => requete<Equipe>('/interventions/equipes', { methode: 'POST', corps });
export const creerPrestataire = (corps: { nom: string; contact?: string; contrat_reference?: string }) => requete<Prestataire>('/interventions/prestataires', { methode: 'POST', corps });
export const trancherConflit = (id: string, decision: DecisionConflitTerrain, motif?: string) =>
  requete<Conflit>(`/terrain/conflits/${id}/trancher`, { methode: 'POST', corps: { decision, ...(motif ? { motif } : {}) } });

/** Photo servie à un agent (contenu binaire derrière le jeton) : adresse d'objet locale à révoquer après usage. */
export async function chargerPhoto(id: string, signal?: AbortSignal): Promise<string> {
  const r = await requete<Response>(`/interventions/photos/${id}`, { brut: true, signal });
  return URL.createObjectURL(await r.blob());
}
